import type { Follow } from "@tandemdiabetes/sugarmate-api-react-client"
import { FollowState } from "@tandemdiabetes/sugarmate-api-react-client"
import { useMatches } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useMemo } from "react"
import { FollowedUsersContext } from "./FollowedUsersContext"
import { useAccount } from "src/hooks/account/useAccount"
import { useDefaultDeviceSettings } from "src/hooks/deviceSettings/useDefaultDeviceSettings"
import { useDeviceSettings } from "src/hooks/deviceSettings/useDeviceSettings"
import { useDefaultFollow } from "src/hooks/follows/useDefaultFollow"
import { useFollows } from "src/hooks/follows/useFollows"

type Props = {
  children?: ReactNode;
}

interface RouteParams {
  accountId?: string;
}

function getCurrentUserId(
  follows: Follow[],
  currentUserId?: Follow["following_account_id"],
) {
  const currentFollow = follows.find((follow) => {
    if (currentUserId === undefined) {
      return follow.is_self
    }

    return follow.following_account_id === currentUserId
  })

  if (currentFollow) {
    return currentFollow.following_account_id
  }

  return undefined
}

/*
 * the FollowedUsersProvider gets the user accounts that the user is following
 */
export function FollowedUsersProvider(props: Props) {
  const {
    data: account,
    isLoading: isLoadingAccount,
  } = useAccount()

  const {
    data: follows,
    isLoading: isLoadingFollows,
  } = useFollows()

  const matches = useMatches()
  const accountId = (matches[matches.length - 1].params as RouteParams)
    .accountId
  const followingAccountId = accountId ? Number(accountId) : undefined

  const defaultFollow = useDefaultFollow()
  const defaultDeviceSettings = useDefaultDeviceSettings()

  // follows that represent this account following someone
  const activeFollows = useMemo(
    () => (follows ?? []).filter(
      (follow) => follow.account_id === account?.id &&
          (follow.state === FollowState.Active ||
            follow.state === FollowState.Provisioned) &&
          follow.deleted_at === null,
    ),
    [
      follows,
      account?.id,
    ],
  )

  // this will be undefined if we can't find the corresponding follow for followingAccountId
  const currentUserId = useMemo(
    () => getCurrentUserId(
      activeFollows,
      followingAccountId,
    ),
    [
      activeFollows,
      followingAccountId,
    ],
  )

  const currentDeviceSettings =
    useDeviceSettings(currentUserId ?? -1) ?? defaultDeviceSettings

  const value = useMemo(
    () => {
      let selfFollow =
        activeFollows.find((follow) => follow.is_self) ?? defaultFollow

      // use the account first and last name in place of the follow first and last name
      selfFollow = {
        ...selfFollow,
        sharer_firstname: account?.firstname ?? selfFollow.sharer_firstname,
        sharer_lastname: account?.lastname ?? selfFollow.sharer_lastname,
      }

      let currentFollow =
        activeFollows.find(
          (follow) => follow.following_account_id === currentUserId,
        ) ?? defaultFollow

      if (currentFollow.is_self) {
        currentFollow = selfFollow
      }

      return {
        currentUserId,
        follows: activeFollows,
        selfFollow,
        currentFollow,
        currentDeviceSettings:
        currentDeviceSettings.following_account_id !== currentUserId
          ? defaultDeviceSettings
          : currentDeviceSettings,
        isFollowOnlyWithNoFollows:
        selfFollow.is_follow_only && activeFollows.length <= 1,
        isLoading: isLoadingAccount || isLoadingFollows,
      }
    },
    [
      account,
      activeFollows,
      currentUserId,
      defaultFollow,
      currentDeviceSettings,
      defaultDeviceSettings,
      isLoadingAccount,
      isLoadingFollows,
    ],
  )

  return (
    <FollowedUsersContext.Provider value={value}>
      {props.children}
    </FollowedUsersContext.Provider>
  )
}
